import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

const BillingGateContext = createContext();
const { Provider } = BillingGateContext;

export const initialBillingGateState = {
  clubId: null,
  billing: null,
  loading: false,
  loaded: false,
  error: null,
};

export const canViewClubBilling = (state, clubId) =>
  !!clubId &&
  state?.clubId === String(clubId) &&
  state?.loaded === true &&
  !state?.error &&
  state?.billing?.billingEnabled !== false &&
  state?.billing?.canViewBilling === true;

export const BillingGateProvider = ({ children }) => {
  const [state, setState] = useState(initialBillingGateState);
  const activeRequestRef = useRef(0);

  const beginBillingRequest = useCallback((clubId) => {
    const normalizedClubId = String(clubId);
    const requestId = activeRequestRef.current + 1;
    activeRequestRef.current = requestId;

    setState((current) => ({
      clubId: normalizedClubId,
      billing: current.clubId === normalizedClubId ? current.billing : null,
      loading: true,
      loaded: current.clubId === normalizedClubId ? current.loaded : false,
      error: null,
    }));

    return requestId;
  }, []);

  const completeBillingRequest = useCallback((requestId, clubId, billing) => {
    if (activeRequestRef.current !== requestId) return false;

    const normalizedClubId = String(clubId);
    setState({
      clubId: normalizedClubId,
      billing,
      loading: false,
      loaded: true,
      error: null,
    });
    return true;
  }, []);

  const failBillingRequest = useCallback((requestId, clubId, error) => {
    if (activeRequestRef.current !== requestId) return false;

    setState({
      clubId: String(clubId),
      billing: null,
      loading: false,
      loaded: true,
      error,
    });
    return true;
  }, []);

  const resetBilling = useCallback(() => {
    activeRequestRef.current += 1;
    setState(initialBillingGateState);
  }, []);

  return (
    <Provider
      value={{
        state,
        beginBillingRequest,
        completeBillingRequest,
        failBillingRequest,
        resetBilling,
      }}
    >
      {children}
    </Provider>
  );
};

export const useBillingGate = () => {
  const ctx = useContext(BillingGateContext);
  if (!ctx) throw new Error("useBillingGate must be used inside provider");
  return ctx;
};
